import { useState, memo, useCallback } from 'react';

import PropTypes from 'prop-types';

import { useClickstreamCallback } from '@zenbusiness/application-commons-clickstream';

import ThreadListItem from '../ThreadListItem';
import * as Styled from './ThreadDateGroup.style';
import { CLICKSTREAM_COMMON_PROPS } from '../utils/constants';
/**
 * Date-grouped thread sections component that organizes threads by time periods
 * @param {object} props - Component props
 * @param {string} props.label - Display label for the date group (e.g., "Today", "Yesterday")
 * @param {Array} props.threads - Array of threads for this date group
 * @param {string} props.currentThreadUuid - UUID of the currently active thread
 * @param {Function} props.onThreadSelect - Callback when a thread is selected
 * @param {boolean} [props.defaultExpanded=true] - Whether the group starts expanded
 * @param {boolean} [props.collapsible=true] - Whether the group can be collapsed
 * @param {Function} [props.onError] - Callback when an error occurs
 * @returns {import('react').ReactElement|null} The ThreadDateGroup component
 */
function ThreadDateGroup({
  label,
  threads,
  currentThreadUuid,
  onThreadSelect,
  defaultExpanded = true,
  collapsible = true,
  onError
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const trackButtonPressed = useClickstreamCallback('buttonPressed');

  const toggleExpanded = () => {
    if (collapsible) {
      setIsExpanded((prev) => !prev);
    }
  };

  const handleKeyDown = (event) => {
    if (collapsible && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      toggleExpanded();
    }
  };

  // Track thread select
  const handleThreadSelect = useCallback(
    (threadUuid) => {
      trackButtonPressed({
        ...CLICKSTREAM_COMMON_PROPS,
        elementName: 'thread_select',
        elementText: `Thread select ${threadUuid}`
      });
      onThreadSelect(threadUuid);
    },
    [onThreadSelect, trackButtonPressed]
  );

  if (!threads || threads.length === 0) {
    return null;
  }

  return (
    <Styled.GroupContainer
      data-testid={`thread-date-group-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Styled.GroupHeader
        aria-expanded={collapsible ? isExpanded : undefined}
        aria-label={collapsible ? `${isExpanded ? 'Collapse' : 'Expand'} ${label} threads` : label}
        collapsible={collapsible}
        data-testid="group-header"
        role={collapsible ? 'button' : undefined}
        tabIndex={collapsible ? 0 : undefined}
        onClick={toggleExpanded}
        onKeyDown={handleKeyDown}
      >
        <Styled.GroupLabel size="xs">{label}</Styled.GroupLabel>

        <Styled.GroupMeta>
          {/* <Styled.ThreadCount>{threads.length}</Styled.ThreadCount> */}

          {collapsible && <Styled.ExpanIcon data-testid="expand-icon" isExpanded={isExpanded} />}
        </Styled.GroupMeta>
      </Styled.GroupHeader>

      <Styled.GroupContent data-testid="group-content" isExpanded={isExpanded}>
        <Styled.ThreadList>
          {threads.map((thread) => (
            <ThreadListItem
              key={thread.threadUuid}
              isActive={thread.threadUuid === currentThreadUuid}
              thread={thread}
              onError={onError}
              onSelect={handleThreadSelect}
            />
          ))}
        </Styled.ThreadList>
      </Styled.GroupContent>
    </Styled.GroupContainer>
  );
}

ThreadDateGroup.propTypes = {
  label: PropTypes.string.isRequired,
  threads: PropTypes.arrayOf(
    PropTypes.shape({
      threadUuid: PropTypes.string.isRequired,
      title: PropTypes.string,
      createdAt: PropTypes.string.isRequired,
      lastMessageAt: PropTypes.string,
      messageCount: PropTypes.number,
      snippet: PropTypes.string
    })
  ).isRequired,
  currentThreadUuid: PropTypes.string,
  onThreadSelect: PropTypes.func.isRequired,
  defaultExpanded: PropTypes.bool,
  collapsible: PropTypes.bool,
  onError: PropTypes.func
};

// Memoize to prevent unnecessary re-renders
export default memo(ThreadDateGroup);
